import type {
  AssignedLocationsTableQueryParams,
  LocationsTableQueryParams,
} from "@/components/table/views/screener-locations/screener-locations-columns";
import type { ScreenersTableQueryParams } from "@/components/table/views/screeners/screeners-columns";
import type { ResultsTableQueryParams } from "@/components/table/views/job/job-columns";
import type {
  AssignedLocationsAvailableFilters,
  LocationsAvailableFilters,
} from "@/types/screener-location";
import type {
  AllScreenersResponse,
  ResultsAvailableFilters,
  ScreenerOrArchivedScreener,
} from "@/types/screeners";
import { coerceLanguageCode } from "@/utils/language-labels";

import type { StaleFilterCleaner, StaleFilterCleanerResult } from "./types";

/**
 * Helper: Filters an array of values against available options.
 * Returns the filtered array and whether any values were removed.
 */
const filterStaleValues = <T>(
  current: T[] | undefined,
  available: T[],
): { filtered: T[] | undefined; hadStale: boolean } => {
  if (!current?.length) {
    return { filtered: undefined, hadStale: false };
  }

  const availableSet = new Set(available);
  const filtered = current.filter((v) => availableSet.has(v));

  return {
    filtered: filtered.length > 0 ? filtered : undefined,
    hadStale: filtered.length !== current.length,
  };
};

type ScreenersAvailableFilters =
  AllScreenersResponse<ScreenerOrArchivedScreener>["availableFilters"];

/**
 * Cleaner for the Screeners table (both active and archived).
 * Handles: jobTitle, location, status, type, userId
 */
export const cleanScreenersFilters: StaleFilterCleaner<
  ScreenersTableQueryParams,
  ScreenersAvailableFilters
> = (
  currentParams,
  availableFilters,
): StaleFilterCleanerResult<ScreenersTableQueryParams> => {
  const checks = [
    {
      key: "jobTitle" as const,
      result: filterStaleValues(
        currentParams.jobTitle,
        availableFilters.jobTitles,
      ),
    },
    {
      key: "location" as const,
      result: filterStaleValues(
        currentParams.location,
        availableFilters.locations,
      ),
    },
    {
      key: "status" as const,
      result: filterStaleValues(
        currentParams.status,
        availableFilters.statuses,
      ),
    },
    {
      key: "type" as const,
      result: filterStaleValues(currentParams.type, availableFilters.types),
    },
    {
      key: "userId" as const,
      result: filterStaleValues(
        currentParams.userId,
        availableFilters.users.map((u) => u.id),
      ),
    },
  ];

  return checks.reduce<StaleFilterCleanerResult<ScreenersTableQueryParams>>(
    (acc, { key, result }) => ({
      cleaned: { ...acc.cleaned, [key]: result.filtered },
      hadStaleValues: acc.hadStaleValues || result.hadStale,
    }),
    {
      cleaned: { ...currentParams },
      hadStaleValues: false,
    },
  );
};

/**
 * Cleaner for the Locations table (single screener view).
 * Handles: locationName, status, assignedTo
 */
export const cleanLocationsFilters: StaleFilterCleaner<
  LocationsTableQueryParams,
  LocationsAvailableFilters
> = (
  currentParams,
  availableFilters,
): StaleFilterCleanerResult<LocationsTableQueryParams> => {
  const checks = [
    {
      key: "locationName" as const,
      result: filterStaleValues(
        currentParams.locationName,
        availableFilters.locationNames,
      ),
    },
    {
      key: "status" as const,
      result: filterStaleValues(
        currentParams.status,
        availableFilters.statuses,
      ),
    },
    {
      key: "assignedTo" as const,
      result: filterStaleValues(
        currentParams.assignedTo,
        availableFilters.assignedToUsers.map((u) => u.id),
      ),
    },
  ];

  return checks.reduce<StaleFilterCleanerResult<LocationsTableQueryParams>>(
    (acc, { key, result }) => ({
      cleaned: { ...acc.cleaned, [key]: result.filtered },
      hadStaleValues: acc.hadStaleValues || result.hadStale,
    }),
    {
      cleaned: { ...currentParams },
      hadStaleValues: false,
    },
  );
};

/**
 * Cleaner for the Assigned Locations table ("My Locations" view).
 * Handles: locationName, status, assignedTo, jobName
 */
export const cleanAssignedLocationsFilters: StaleFilterCleaner<
  AssignedLocationsTableQueryParams,
  AssignedLocationsAvailableFilters
> = (
  currentParams,
  availableFilters,
): StaleFilterCleanerResult<AssignedLocationsTableQueryParams> => {
  const checks = [
    {
      key: "locationName" as const,
      result: filterStaleValues(
        currentParams.locationName,
        availableFilters.locationNames,
      ),
    },
    {
      key: "status" as const,
      result: filterStaleValues(
        currentParams.status,
        availableFilters.statuses,
      ),
    },
    {
      key: "jobName" as const,
      result: filterStaleValues(
        currentParams.jobName,
        availableFilters.jobNames,
      ),
    },
  ];

  return checks.reduce<
    StaleFilterCleanerResult<AssignedLocationsTableQueryParams>
  >(
    (acc, { key, result }) => ({
      cleaned: { ...acc.cleaned, [key]: result.filtered },
      hadStaleValues: acc.hadStaleValues || result.hadStale,
    }),
    {
      cleaned: { ...currentParams },
      hadStaleValues: false,
    },
  );
};

// Results table has dynamic keys that need special handling
type ResultsParamsWithDynamicKeys = ResultsTableQueryParams &
  Record<string, unknown>;

/**
 * Cleaner for the Results table (Job/Candidate results).
 * Handles: highlightColor, language, and dynamic competencies.* / showInSummary.* keys
 */
export const cleanResultsFilters: StaleFilterCleaner<
  ResultsParamsWithDynamicKeys,
  ResultsAvailableFilters
> = (
  currentParams,
  availableFilters,
): StaleFilterCleanerResult<ResultsParamsWithDynamicKeys> => {
  // Standard array filters
  const standardChecks = [
    {
      key: "highlightColor" as const,
      result: filterStaleValues(
        currentParams.highlightColor,
        availableFilters.highlightColors,
      ),
    },
    {
      key: "language" as const,
      result: filterStaleValues(
        currentParams.language,
        availableFilters.languages.map(coerceLanguageCode),
      ),
    },
    {
      key: "candidateSource" as const,
      result: filterStaleValues(
        currentParams.candidateSource,
        availableFilters.candidateSources ?? [],
      ),
    },
  ];

  const afterStandardChecks = standardChecks.reduce<
    StaleFilterCleanerResult<ResultsParamsWithDynamicKeys>
  >(
    (acc, { key, result }) => ({
      cleaned: { ...acc.cleaned, [key]: result.filtered },
      hadStaleValues: acc.hadStaleValues || result.hadStale,
    }),
    {
      cleaned: { ...currentParams },
      hadStaleValues: false,
    },
  );

  // Check dynamic competency keys (competencies.*)
  const availableCompetencies = new Set(availableFilters.competencies);
  const availableShowInSummary = new Set(availableFilters.showInSummaryFields);

  const isStaleCompetencyKey = (key: string): boolean =>
    key.startsWith("competencies.") &&
    !availableCompetencies.has(key.slice("competencies.".length));

  const isStaleShowInSummaryKey = (key: string): boolean =>
    key.startsWith("showInSummary.") &&
    !availableShowInSummary.has(key.slice("showInSummary.".length));

  const staleKeys = Object.keys(afterStandardChecks.cleaned).filter(
    (key) => isStaleCompetencyKey(key) || isStaleShowInSummaryKey(key),
  );

  if (staleKeys.length === 0) {
    return afterStandardChecks;
  }

  const staleKeySet = new Set(staleKeys);
  const cleaned = Object.fromEntries(
    Object.entries(afterStandardChecks.cleaned).filter(
      ([key]) => !staleKeySet.has(key),
    ),
  ) as Partial<ResultsParamsWithDynamicKeys>;

  return {
    cleaned,
    hadStaleValues: true,
  };
};
